import React, { Component } from 'react'

import find from 'lodash/find'
import get from 'lodash/get'
import head from 'lodash/head'
import max from 'lodash/max'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import StatCountUp from '../../../../components/ui/StatCountUp'
import searches from '../../../../utils/routing/searches'
import { capitalize } from '../../../../utils/string'
import numberConverter from '../../../../utils/tuition/numberForCurrency'

import VerticalBarChart from './VerticalBarChart'
import VerticalBarCharWithSkillDescription from './VerticalBarChartWithSkillDescription'

const normalizeValues = arr => {
  const maxVal = get(head(arr), 'doc_count', 0)
  const maxSal = max(arr.map(sal => get(sal, 'metric')))

  return arr.map(o => {
    const obj = { ...o }
    obj['doc_count'] = (o['doc_count'] * 100) / maxVal
    obj['metric'] = (o['metric'] * 100) / maxSal
    obj['rawMetric'] = o['metric']
    return obj
  })
}

class Outcomes extends Component {
  static propTypes = {
    handleClearFilters: PropTypes.func,
    handleGenderClick: PropTypes.func,
    handleCompanyClick: PropTypes.func,
    handleEduCategoryClick: PropTypes.func,
    handleIndustryClick: PropTypes.func,
    handleSkillClick: PropTypes.func,
    isClearingFilters: PropTypes.bool,
    selectedCompany: PropTypes.string,
    selectedIndustry: PropTypes.string,
    selectedMajor: PropTypes.string,
    selectedSkill: PropTypes.string,
    isMobile: PropTypes.bool,
    univData: PropTypes.object,
  }

  render() {
    const {
      handleClearFilters,
      handleCompanyClick,
      handleEduCategoryClick,
      handleIndustryClick,
      handleSkillClick,
      isClearingFilters,
      isMobile,
      selectedCompany,
      selectedIndustry,
      selectedMajor,
      selectedSkill,
      univData,
      shortName,
    } = this.props

    const totalCount = get(univData, 'doc_count')
    const clickFacets = get(univData, 'click_facets')
    const employers = normalizeValues(get(clickFacets, 'click_current_employers', []))
    const degrees = normalizeValues(get(clickFacets, 'click_majors', []))
    const jobCategories = normalizeValues(get(clickFacets, 'click_industries', []))
    const skills = normalizeValues(get(clickFacets, 'click_skills', []))

    const genders = get(univData, 'gender', [])
    const malesCount = get(
      find(genders, o => get(o, 'key') === 'm'),
      'doc_count',
      0,
    )
    const femalesCount = get(
      find(genders, o => get(o, 'key') === 'f'),
      'doc_count',
      0,
    )
    const malesPct = malesCount ? (malesCount / (malesCount + femalesCount)) * 100 : 0
    const dividedCommonJobs = []
    univData &&
      univData.commonJobs &&
      univData.commonJobs.forEach((item, index) => {
        if (index % 2 !== 0) {
          dividedCommonJobs.push([univData.commonJobs[index - 1], univData.commonJobs[index]])
        }
        if (index === univData.commonJobs.length - 1 && (index % 2 === 0 || index === 0)) {
          dividedCommonJobs.push([univData.commonJobs[index]])
        }
      })

    return (
      <div>
        <div className="card shadow">
          <div className="row m-0">
            <div
              className="col-12"
              style={{
                padding: isMobile ? 10 : 20,
                paddingBottom: 20,
                marginTop: 10,
              }}
            >
              <div className="text-center" style={{ fontSize: 22, color: '#5099de' }}>
                {capitalize(shortName)} Graduates
              </div>
              <div className="text-center text-muted max-1000">
                Click any major, employer, job category, and/or skill to explore outcomes.
              </div>
            </div>
          </div>
          <div className="row m-0">
            <div
              className="text-center"
              style={{
                position: 'absolute',
                top: isMobile ? 55 : 33,
                right: isMobile ? 5 : 24,
                cursor: 'pointer',
              }}
              onClick={handleClearFilters}
            >
              <i
                className="info-button fas fa-sync-alt pb-1"
                style={{ color: '#ff963a', fontSize: isMobile ? 12 : 18 }}
              />
              <div style={{ fontSize: isMobile ? 8 : 10, color: '#ff963a' }}>RESET</div>
            </div>
          </div>
          <div className="row m-0" id="filters">
            <VerticalBarChart
              title="With a Degree in"
              data={!isClearingFilters ? degrees : []}
              selectedValue={selectedMajor}
              onBarClick={handleEduCategoryClick}
              isMobile={isMobile}
              keyForValue="doc_count"
              showInsight
              capitalize={capitalize}
            />
            <VerticalBarChart
              title="I could be hired by"
              data={!isClearingFilters ? employers : []}
              selectedValue={selectedCompany}
              onBarClick={handleCompanyClick}
              keyForValue="doc_count"
              isMobile={isMobile}
              capitalize={capitalize}
              showInsight={false}
            />
            {!isMobile && (
              <VerticalBarChart
                title="Working in"
                data={!isClearingFilters ? jobCategories : []}
                selectedValue={selectedIndustry}
                onBarClick={handleIndustryClick}
                keyForValue="doc_count"
                isMobile={isMobile}
                capitalize={capitalize}
                showInsight={false}
              />
            )}
            {!isMobile && (
              <VerticalBarCharWithSkillDescription
                title="Skilled in"
                selectedValue={selectedSkill}
                data={!isClearingFilters ? skills : []}
                onBarClick={handleSkillClick}
                isMobile={isMobile}
                keyForValue="doc_count"
                infoContent="Sorted by relevance"
                capitalize={capitalize}
                showInsight={false}
              />
            )}
          </div>

          <div
            className="row m-0"
            style={{ borderTop: '1px solid rgb(231, 236, 241)', minHeight: 110 }}
          >
            <div
              className="col-md-4"
              style={{
                borderRight: '1px solid rgb(231, 236, 241)',
                paddingTop: 20,
                paddingBottom: 20,
              }}
            >
              <div className="row">
                <h6 className="outcomes-header" style={{ marginBottom: 0 }}>
                  Graduates Analyzed
                </h6>
                <div className="col-8 pr-0">
                  <StatCountUp
                    data={totalCount}
                    isMobile={isMobile}
                    color="#686c6f"
                    alignment="text-right"
                  />
                </div>
                <div className="col-4">
                  <div className="row" style={{ fontSize: 20, paddingTop: 6 }}>
                    <div className="col-4 text-right p-0">
                      <i className="fal fa-mars" style={{ paddingRight: 4, color: '#0bbbf5' }} />
                    </div>
                    <div className="col-8 p-0" style={{ color: '#0bbbf5' }}>
                      {`${numberConverter(malesPct)}%`}
                    </div>
                    <div className="col-4 text-right p-0">
                      <i
                        className="fal fa-venus"
                        style={{ paddingRight: 7, color: 'rgb(230, 54, 158)' }}
                      />
                    </div>
                    <div className="col-8 p-0" style={{ color: 'rgb(230, 54, 158)' }}>
                      {`${numberConverter(100 - malesPct)}%`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="col-md-4"
              style={{
                borderRight: '1px solid rgb(231, 236, 241)',
                paddingTop: 20,
                paddingBottom: 20,
              }}
            >
              <h6 className="outcomes-header" style={{ marginBottom: 0 }}>
                Average 5 year Salary
              </h6>
              {get(univData, 'avg_salary_5_year') > get(univData, 'avg_salary') ||
              get(univData, 'avg_salary_5_year') < 20000 ? (
                <div className="text-muted text-center" style={{ paddingTop: 24 }}>
                  Not Enough Data
                </div>
              ) : (
                <StatCountUp
                  data={get(univData, 'avg_salary_5_year')}
                  isMobile={isMobile}
                  prefix="$"
                />
              )}
            </div>
            <div className="col-md-4" style={{ paddingTop: 20, paddingBottom: 20 }}>
              <h6 className="outcomes-header" style={{ marginBottom: 0 }}>
                Average Current Salary
              </h6>
              <StatCountUp data={get(univData, 'avg_salary')} isMobile={isMobile} prefix="$" />
            </div>
          </div>
        </div>
        {dividedCommonJobs && dividedCommonJobs.length >= 2 && (
          <div className="row justify-content-md-center text-center" style={{ marginTop: 30 }}>
            <div className="col-12">Common Jobs</div>
            {dividedCommonJobs.map((result, index) => (
              <div
                key={result[0].key}
                style={
                  isMobile ? { width: '100%', marginRight: 16 } : { width: '90%', display: 'flex' }
                }
              >
                <div className="col-md-6 text-center" style={{ paddingRight: 0, marginRight: 0 }}>
                  <div key={`job${index}`} className="mt-2">
                    <Link
                      to={searches.buildUrlForSavedEducationSearch({
                        searchParams: {
                          searchType: 'career',
                          search_query: result[0].key,
                        },
                      })}
                    >
                      <i className="fal fa-search" /> {capitalize(result[0].key)}
                    </Link>
                  </div>
                </div>
                {result.length > 1 && (
                  <div className="col-md-6 text-center">
                    <div key={`job${index}`} className="mt-2">
                      <Link
                        to={searches.buildUrlForSavedEducationSearch({
                          searchParams: {
                            searchType: 'career',
                            search_query: result[1].key,
                          },
                        })}
                      >
                        <i className="fal fa-search" /> {capitalize(result[1].key)}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
}

export default Outcomes



// WEBPACK FOOTER //
// ./src/routes/authenticated/Outcomes/components/Outcomes.js